import { useState, useEffect, useRef } from "react";
import type { InfoPointNode } from "../script/TourDataStruct";
import styles from "../styles/InfoPointModal.module.css";
import ReactMarkdown from 'react-markdown';

interface InfoPointModalProps {
    infoPoint: InfoPointNode;
    onClose: () => void;
}

function InfoPointModal({ infoPoint, onClose }: InfoPointModalProps) {
    const [imageExpanded, setImageExpanded] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    const handleSpeak = () => {
        if (isSpeaking) {
            if (infoPoint.audioSrc) {
                audioRef.current?.pause();
            } else {
                speechSynthesis.cancel();
            }
            setIsSpeaking(false);
            return;
        }

        if (infoPoint.audioSrc) {
            const audio = new Audio(infoPoint.audioSrc);
            audio.onended = () => setIsSpeaking(false);
            audioRef.current = audio;
            audio.play();
        } else {
            const utterance = new SpeechSynthesisUtterance(removeMDSymbolsForTTS(infoPoint.description));
            utterance.onend = () => setIsSpeaking(false);
            speechSynthesis.speak(utterance);
        }
        setIsSpeaking(true);
    };

    const removeMDSymbolsForTTS = (text: string) => {
        const lines = text.replace(/[|*]/g, '').split('\n');
        for (let i = 0; i < lines.length; i++) {
            if (lines[i].startsWith('#')) {
                lines[i] = lines[i].replace(/^#+\s*/, '');
            }
        }
        const result = lines.join('\n');
        return result;
    }

    useEffect(() => {
        return () => {
            speechSynthesis.cancel();
            audioRef.current?.pause();
        };
    }, []);

    return (
        <div className={styles.backdrop} onClick={onClose}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
                <div className={styles.header}>
                    <h2 className={styles.title}>{infoPoint.title}</h2>
                    <button onClick={onClose} className={styles.closeBtn}>&times;</button>
                </div>
                <div className={styles.body}>
                    <div className={styles.description}>
                        <ReactMarkdown>{infoPoint.description.replace(/\n/g, '  \n')}</ReactMarkdown>
                    </div>
                    <div className={styles.mediaRow}>
                        {infoPoint.imageSrc && (
                            <div>
                                <img
                                    src={infoPoint.imageSrc.replace('.jpg', '-small.jpg')}
                                    alt={infoPoint.title}
                                    className={styles.image}
                                    onClick={() => setImageExpanded(true)}
                                    draggable={false}
                                />
                                <p className={styles.imageHint}>Click image to expand</p>
                            </div>
                        )}
                        {infoPoint.description && (
                            <button className={styles.speakBtn} onClick={handleSpeak}>
                                {isSpeaking ? "Stop Audio" : "Play Audio"}
                            </button>
                        )}
                    </div>
                    {imageExpanded && (
                        <div className={styles.imageOverlay} onClick={() => setImageExpanded(false)}>
                            <img src={infoPoint.imageSrc} alt={infoPoint.title} draggable={false} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default InfoPointModal;